/**
 * Streaming Speech Server Type Definitions
 *
 * TypeScript interfaces aligned with the new TypeScript streaming speech server API.
 * These types match the server's message types and error codes exactly.
 */

/**
 * Error Code Enumeration
 * Matches server ErrorCodes.ts exactly
 */
export enum StreamingSpeechErrorCode {
  // Service Availability (Critical - Blocks Operation)
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  SERVICE_DEGRADED = 'SERVICE_DEGRADED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  AUTH_FAILED = 'AUTH_FAILED',

  // Service Availability (Warning - Partial Function)
  LLM_UNAVAILABLE = 'LLM_UNAVAILABLE',
  LLM_AUTH_FAILED = 'LLM_AUTH_FAILED',
  LLM_INIT_FAILED = 'LLM_INIT_FAILED',

  // Network Issues
  NETWORK_ERROR = 'NETWORK_ERROR',
  CONNECTION_LOST = 'CONNECTION_LOST',
  KEEPALIVE_TIMEOUT = 'KEEPALIVE_TIMEOUT',

  // Session Management
  SESSION_NOT_FOUND = 'SESSION_NOT_FOUND',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MAX_SESSIONS_EXCEEDED = 'MAX_SESSIONS_EXCEEDED',
  INVALID_SESSION_STATE = 'INVALID_SESSION_STATE',

  // Processing Errors
  SPEECH_STREAM_ERROR = 'SPEECH_STREAM_ERROR',
  SPEECH_PROCESSING_FAILED = 'SPEECH_PROCESSING_FAILED',
  LLM_PROCESSING_FAILED = 'LLM_PROCESSING_FAILED',
  AUDIO_PROCESSING_ERROR = 'AUDIO_PROCESSING_ERROR',

  // Timeouts
  INITIALIZATION_TIMEOUT = 'INITIALIZATION_TIMEOUT',
  PROCESSING_TIMEOUT = 'PROCESSING_TIMEOUT',
  LLM_TIMEOUT = 'LLM_TIMEOUT',
  SESSION_TIMEOUT = 'SESSION_TIMEOUT',

  // Validation
  INVALID_CONFIG = 'INVALID_CONFIG',
  INVALID_AUDIO = 'INVALID_AUDIO',
  INVALID_MESSAGE = 'INVALID_MESSAGE',

  // Resource Limits
  MAX_AUDIO_SIZE_EXCEEDED = 'MAX_AUDIO_SIZE_EXCEEDED',
  MAX_DURATION_EXCEEDED = 'MAX_DURATION_EXCEEDED',
  MEMORY_LIMIT_EXCEEDED = 'MEMORY_LIMIT_EXCEEDED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',

  // Internal Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  STATE_MACHINE_ERROR = 'STATE_MACHINE_ERROR',

  // Legacy error codes
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  STREAM_START_ERROR = 'STREAM_START_ERROR',
  AUDIO_CHUNK_ERROR = 'AUDIO_CHUNK_ERROR',
  BATCH_RECOGNITION_ERROR = 'BATCH_RECOGNITION_ERROR',
  GOOGLE_API_ERROR = 'GOOGLE_API_ERROR',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  INVALID_AUDIO_FORMAT = 'INVALID_AUDIO_FORMAT',
  LLM_CONFIG_INVALID = 'LLM_CONFIG_INVALID',
}

export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';
export type ErrorCategory = 'service' | 'network' | 'session' | 'processing' | 'validation' | 'resource';
export type SuggestedAction = 'retry' | 'fallback' | 'wait' | 'contact_admin' | 'continue' | 'use_raw_transcript' | 'reconnect';

/**
 * LLM Configuration
 */
export interface LLMConfig {
  enabled: boolean;
  model: string;
  prompt: string;
  promptName?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
}

/**
 * Base interface for all WebSocket messages
 */
export interface BaseStreamingMessage {
  type: string;
  sessionId?: string;
  timestamp: string;
}

/**
 * Client-to-Server Messages
 */

export interface StartMessage extends BaseStreamingMessage {
  type: 'start';
  processingMode: 'streaming' | 'batch';
  model?: string;
  sampleRate?: number;
  encoding?: 'LINEAR16' | 'WEBM_OPUS'; // Optional, defaults to LINEAR16 for backward compatibility
  patientId?: string;
  patientUuid?: string;
  practiceId?: string;
  practiceTenantId?: string;
  fieldContext?: string;
  componentName?: string;
  maxRecordingTime?: number;
  llmConfig?: LLMConfig;
  parentEventId?: string;
}

export interface StopMessage extends BaseStreamingMessage {
  type: 'stop';
}

export interface CancelMessage extends BaseStreamingMessage {
  type: 'cancel';
  reason?: string;
}

export interface PingMessage extends BaseStreamingMessage {
  type: 'ping';
  sequence?: number;
}

export type ClientMessage = StartMessage | StopMessage | CancelMessage | PingMessage;

/**
 * Server-to-Client Messages
 */

export interface ConnectionMessage extends BaseStreamingMessage {
  type: 'connection';
  status: 'connected' | 'disconnected';
  message: string;
}

export interface StreamStartedMessage extends BaseStreamingMessage {
  type: 'stream_started';
  model: string;
  api?: string;
  region?: string;
  processingMode: 'streaming' | 'batch';
  maxRecordingTime?: number;
  message?: string;
  config?: any;
}

export interface TranscriptMessage extends BaseStreamingMessage {
  type: 'transcript';
  transcript: string;
  confidence: number;
  isFinal: boolean;
  wordTimeOffsets?: Array<{
    word: string;
    startTime: string;
    endTime: string;
  }>;
}

export interface FinalTranscriptMessage extends BaseStreamingMessage {
  type: 'final_transcript';
  transcript: string;
  confidence?: number;
}

export interface ProcessedTranscriptMessage extends BaseStreamingMessage {
  type: 'processed_transcript';
  rawTranscript: string;
  processedTranscript: string;
  llmModel: string;
  processingTime: number;
}

export interface StreamEndedMessage extends BaseStreamingMessage {
  type: 'stream_ended';
  reason?: 'completed' | 'max_time_exceeded' | 'cancelled' | 'error' | 'llm_error' | 'timeout';
}

export interface ErrorMessage extends BaseStreamingMessage {
  type: 'error';
  code: StreamingSpeechErrorCode;
  error: string;
  severity?: 'critical' | 'error';
  recoverable?: boolean;
  suggestedAction?: 'retry' | 'wait' | 'contact_admin' | 'reconnect' | 'fallback';
  retryAfterMs?: number;
  details?: string;
  partialResults?: any;
  rawTranscript?: string;
}

export interface WarningMessage extends BaseStreamingMessage {
  type: 'warning';
  code: StreamingSpeechErrorCode;
  message: string;
  severity: 'warning' | 'info';
  recoverable: boolean;
  suggestedAction: 'continue' | 'use_raw_transcript';
  rawTranscript?: string;
}

export interface LLMErrorMessage extends BaseStreamingMessage {
  type: 'llm_error';
  code: StreamingSpeechErrorCode.LLM_TIMEOUT | StreamingSpeechErrorCode.LLM_PROCESSING_FAILED | StreamingSpeechErrorCode.LLM_INIT_FAILED;
  error: string;
  rawTranscript: string;
}

export interface MaxTimeReachedMessage extends BaseStreamingMessage {
  type: 'max_time_reached';
  maxRecordingTime: number;
  message: string;
  gracePeriodSeconds: number;
}

export interface TimeoutMessage extends BaseStreamingMessage {
  type: 'timeout';
  code: StreamingSpeechErrorCode;
  message: string;
  timeoutMs: number;
}

export interface CancelledMessage extends BaseStreamingMessage {
  type: 'cancelled';
  reason: string;
}

export interface PongMessage extends BaseStreamingMessage {
  type: 'pong';
  sequence?: number;
}

/**
 * Union type for all server messages
 */
export type StreamingSpeechMessage =
  | ConnectionMessage
  | StreamStartedMessage
  | TranscriptMessage
  | FinalTranscriptMessage
  | ProcessedTranscriptMessage
  | StreamEndedMessage
  | ErrorMessage
  | WarningMessage
  | LLMErrorMessage
  | MaxTimeReachedMessage
  | TimeoutMessage
  | CancelledMessage
  | PongMessage;

/**
 * Type guard functions
 */
export function isConnectionMessage(msg: StreamingSpeechMessage): msg is ConnectionMessage {
  return msg.type === 'connection';
}

export function isStreamStartedMessage(msg: StreamingSpeechMessage): msg is StreamStartedMessage {
  return msg.type === 'stream_started';
}

export function isTranscriptMessage(msg: StreamingSpeechMessage): msg is TranscriptMessage {
  return msg.type === 'transcript';
}

export function isFinalTranscriptMessage(msg: StreamingSpeechMessage): msg is FinalTranscriptMessage {
  return msg.type === 'final_transcript';
}

export function isProcessedTranscriptMessage(msg: StreamingSpeechMessage): msg is ProcessedTranscriptMessage {
  return msg.type === 'processed_transcript';
}

export function isStreamEndedMessage(msg: StreamingSpeechMessage): msg is StreamEndedMessage {
  return msg.type === 'stream_ended';
}

export function isErrorMessage(msg: StreamingSpeechMessage): msg is ErrorMessage {
  return msg.type === 'error';
}

export function isWarningMessage(msg: StreamingSpeechMessage): msg is WarningMessage {
  return msg.type === 'warning';
}

export function isLLMErrorMessage(msg: StreamingSpeechMessage): msg is LLMErrorMessage {
  return msg.type === 'llm_error';
}

export function isMaxTimeReachedMessage(msg: StreamingSpeechMessage): msg is MaxTimeReachedMessage {
  return msg.type === 'max_time_reached';
}

export function isTimeoutMessage(msg: StreamingSpeechMessage): msg is TimeoutMessage {
  return msg.type === 'timeout';
}

export function isCancelledMessage(msg: StreamingSpeechMessage): msg is CancelledMessage {
  return msg.type === 'cancelled';
}

/**
 * Streaming Speech Error with full metadata
 */
export interface StreamingSpeechError {
  code: StreamingSpeechErrorCode;
  error: string;
  severity: ErrorSeverity;
  recoverable: boolean;
  suggestedAction: SuggestedAction;
  retryAfterMs?: number;
  details?: string;
  rawTranscript?: string;
}

/**
 * Configuration for starting a streaming speech session
 */
export interface StreamingSpeechConfig {
  processingMode: 'streaming' | 'batch';
  model?: string;
  sampleRate?: number;
  encoding?: 'LINEAR16' | 'WEBM_OPUS'; // Optional, defaults to LINEAR16 for backward compatibility
  patientId?: string;
  patientUuid?: string;
  practiceId?: string;
  practiceTenantId?: string;
  fieldContext?: string;
  componentName?: string;
  maxRecordingTime?: number;
  llmConfig?: LLMConfig;
  parentEventId?: string;
}

/**
 * Connection status type
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
